/**
 * Number Line Utility Functions
 */
import {
  parseInputWithLabel,
  labelStillValid as sharedLabelStillValid,
} from '../shared/inputLabels';

/**
 * Generate a unique ID for elements
 */
export const generateId = () => {
  return `nl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Default number line configuration
 */
export const defaultNumberLineConfig = {
  min: -10,
  max: 10,
  step: 1,
  labelInterval: 1,
  showTicks: true,
  showLabels: true,
  snapToTicks: true,
  allowedModes: ['point'],  // 'point' | 'interval' | 'ray'
  tolerance: 0.01,
  maxElements: 20,
};

/**
 * Convert a value on the number line to pixel position
 * @param {number} value - Value on the number line
 * @param {Object} config - Number line configuration
 * @param {number} width - SVG width (excluding padding)
 * @param {number} padding - Horizontal padding
 * @returns {number} - X position in pixels
 */
export const valueToPixel = (value, config, width, padding = 40) => {
  const { min, max } = config;
  const usableWidth = width - (padding * 2);
  return padding + ((value - min) / (max - min)) * usableWidth;
};

/**
 * Convert pixel position to value on the number line
 * @param {number} pixelX - X position in pixels
 * @param {Object} config - Number line configuration
 * @param {number} width - SVG width
 * @param {number} padding - Horizontal padding
 * @returns {number} - Value on the number line
 */
export const pixelToValue = (pixelX, config, width, padding = 40) => {
  const { min, max, step, snapToTicks = true } = config;
  const usableWidth = width - (padding * 2);

  // Convert pixel to raw value
  let value = min + ((pixelX - padding) / usableWidth) * (max - min);

  // Snap to ticks if enabled
  if (snapToTicks) {
    value = Math.round(value / step) * step;
  }

  // Clamp to bounds
  value = Math.max(min, Math.min(max, value));

  // Round to avoid floating point issues
  value = Math.round(value * 1000) / 1000;

  return value;
};

/**
 * Generate tick marks for the number line
 * @param {Object} config - Number line configuration
 * @returns {Array<number>} - Array of tick values
 */
export const generateTicks = (config) => {
  const { min, max, step } = config;
  const ticks = [];

  if (!step || step <= 0) return ticks;

  for (let v = min; v <= max; v += step) {
    // Round to avoid floating point issues
    ticks.push(Math.round(v * 1000) / 1000);
  }

  return ticks;
};

/**
 * Generate labels for the number line
 * @param {Object} config - Number line configuration
 * @returns {Array<number>} - Array of label values
 */
export const generateLabels = (config) => {
  const { min, max, labelInterval = 1 } = config;
  const labels = [];

  if (!labelInterval || labelInterval <= 0) return labels;

  for (let v = min; v <= max; v += labelInterval) {
    labels.push(Math.round(v * 1000) / 1000);
  }

  return labels;
};

/**
 * Common fractions used in math education
 */
const COMMON_FRACTIONS = [
  { decimal: 0.5, fraction: '1/2' },
  { decimal: 1/3, fraction: '1/3' },
  { decimal: 2/3, fraction: '2/3' },
  { decimal: 0.25, fraction: '1/4' },
  { decimal: 0.75, fraction: '3/4' },
  { decimal: 0.2, fraction: '1/5' },
  { decimal: 0.4, fraction: '2/5' },
  { decimal: 0.6, fraction: '3/5' },
  { decimal: 0.8, fraction: '4/5' },
];

const FRACTION_EPSILON = 0.0001;

/**
 * Convert a decimal to a fraction string if it matches common fractions
 * @param {number} decimal - The decimal value to convert
 * @returns {string} - Fraction string or decimal string
 */
export const decimalToFraction = (decimal) => {
  if (decimal === null || decimal === undefined || isNaN(decimal)) {
    return '0';
  }

  if (Number.isInteger(decimal)) {
    return String(decimal);
  }

  const isNegative = decimal < 0;
  const absDecimal = Math.abs(decimal);
  const wholePart = Math.floor(absDecimal);
  const fractionalPart = absDecimal - wholePart;

  if (fractionalPart < FRACTION_EPSILON) {
    if (wholePart === 0) return '0';
    return isNegative ? `-${wholePart}` : String(wholePart);
  }

  for (const { decimal: d, fraction: f } of COMMON_FRACTIONS) {
    if (Math.abs(fractionalPart - d) < FRACTION_EPSILON) {
      const sign = isNegative ? '-' : '';
      if (wholePart === 0) {
        return `${sign}${f}`;
      }
      const [num, denom] = f.split('/').map(Number);
      const improperNum = wholePart * denom + num;
      return `${sign}${improperNum}/${denom}`;
    }
  }

  const formatted = absDecimal.toFixed(2).replace(/\.?0+$/, '');
  return isNegative ? `-${formatted}` : formatted;
};

/**
 * Format a value for display
 * @param {number} value - Value to format
 * @returns {string} - Formatted string
 */
export const formatValue = (value) => {
  return decimalToFraction(value);
};

/**
 * Parse a string that may be a fraction (e.g. "1/2", "-3/4") or a regular number
 * @param {string} str - The input string
 * @returns {{ valid: boolean, value?: number, error?: string }}
 */
export const parseFraction = (str) => parseInputWithLabel(str);

/**
 * Float comparison with tolerance
 * @param {number} a - First number
 * @param {number} b - Second number
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const floatEquals = (a, b, tolerance = 0.01) => {
  if (typeof a !== 'number' || typeof b !== 'number' ||
      !isFinite(a) || !isFinite(b)) return false;
  return Math.abs(a - b) <= tolerance;
};

/**
 * Check if two points match
 * @param {Object} p1 - First point element
 * @param {Object} p2 - Second point element
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const pointsMatch = (p1, p2, tolerance = 0.01) => {
  if (!p1 || !p2) return false;
  if (p1.type !== 'point' || p2.type !== 'point') return false;
  return floatEquals(p1.value, p2.value, tolerance);
};

/**
 * Check if two intervals match
 * @param {Object} i1 - First interval element
 * @param {Object} i2 - Second interval element
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const intervalsMatch = (i1, i2, tolerance = 0.01) => {
  if (!i1 || !i2) return false;
  if (i1.type !== 'interval' || i2.type !== 'interval') return false;

  return floatEquals(i1.start, i2.start, tolerance) &&
         floatEquals(i1.end, i2.end, tolerance) &&
         i1.startOpen === i2.startOpen &&
         i1.endOpen === i2.endOpen;
};

/**
 * Check if two rays match
 * @param {Object} r1 - First ray element
 * @param {Object} r2 - Second ray element
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const raysMatch = (r1, r2, tolerance = 0.01) => {
  if (!r1 || !r2) return false;
  if (r1.type !== 'ray' || r2.type !== 'ray') return false;

  return floatEquals(r1.origin, r2.origin, tolerance) &&
         r1.direction === r2.direction &&
         r1.originOpen === r2.originOpen;
};

/**
 * Check if two elements match
 * @param {Object} el1 - First element
 * @param {Object} el2 - Second element
 * @param {number} tolerance - Comparison tolerance
 * @returns {boolean}
 */
export const elementsMatch = (el1, el2, tolerance = 0.01) => {
  if (!el1 || !el2) return false;
  if (el1.type !== el2.type) return false;

  switch (el1.type) {
    case 'point':
      return pointsMatch(el1, el2, tolerance);
    case 'interval':
      return intervalsMatch(el1, el2, tolerance);
    case 'ray':
      return raysMatch(el1, el2, tolerance);
    default:
      return false;
  }
};

/**
 * Format element summary for display
 * @param {Object} el - Element to format
 * @returns {string}
 */
export const formatElementSummary = (el) => {
  if (!el || !el.type) return 'Unknown element';

  switch (el.type) {
    case 'point':
      return `Point: ${el.label ?? formatValue(el.value)}`;
    case 'interval': {
      const startBracket = el.startOpen ? '(' : '[';
      const endBracket = el.endOpen ? ')' : ']';
      const startStr = el.startLabel ?? formatValue(el.start);
      const endStr = el.endLabel ?? formatValue(el.end);
      return `Interval: ${startBracket}${startStr}, ${endStr}${endBracket}`;
    }
    case 'ray': {
      const bracket = el.originOpen ? '(' : '[';
      const originStr = el.originLabel ?? formatValue(el.origin);
      if (el.direction === 'left') {
        return `Ray: (-∞, ${originStr}${bracket === '(' ? ')' : ']'}`;
      } else {
        return `Ray: ${bracket}${originStr}, +∞)`;
      }
    }
    default:
      return 'Unknown element';
  }
};

/**
 * Parse a number line answer for display
 * @param {Array|string} answer - Answer data
 * @param {Object} config - Configuration
 * @returns {Object|null}
 */
export const parseNumberLineAnswerForDisplay = (answer, config) => {
  let elements = [];
  try {
    if (answer && Array.isArray(answer) && answer[0]) {
      const parsed = JSON.parse(answer[0]);
      elements = parsed?.elements || [];
    } else if (answer && typeof answer === 'string') {
      const parsed = JSON.parse(answer);
      elements = parsed?.elements || [];
    }
  } catch (e) {
    return null;
  }

  if (elements.length === 0) return null;

  return {
    elements,
    config: config || defaultNumberLineConfig,
  };
};

/**
 * Infer allowed modes from the correct answer elements
 * @param {Object} question - Question object with answer
 * @returns {Array<string>} - Array of allowed modes
 */
export const inferAllowedModesFromAnswer = (question) => {
  if (!question.answer || !question.answer[0]) return ['point'];

  try {
    const parsed = JSON.parse(question.answer[0]);
    if (!parsed.elements || !Array.isArray(parsed.elements)) return ['point'];

    const elementTypes = [...new Set(parsed.elements.map(el => el.type))];
    return elementTypes.length > 0 ? elementTypes : ['point'];
  } catch (e) {
    return ['point'];
  }
};

/**
 * Combine admin-configured allowedModes with answer-inferred modes
 * @param {Object} question - Question object
 * @returns {Array<string>} - Union of modes
 */
export const getUnionAllowedModes = (question) => {
  const configModes = question.answer_config?.allowedModes || [];
  const inferredModes = inferAllowedModesFromAnswer(question);

  const union = [...new Set([...configModes, ...inferredModes])];
  return union.length > 0 ? union : ['point'];
};

/**
 * Normalize infinity tokens in a string to canonical form
 */
const normalizeInfinity = (str) => {
  return str
    // Negative infinity first (to avoid partial matches)
    .replace(/-\s*∞/g, '-INF')
    .replace(/-\s*infinity\b/gi, '-INF')
    .replace(/-\s*inf\b/gi, '-INF')
    // Positive infinity (with optional +)
    .replace(/[+]?\s*∞/g, 'INF')
    .replace(/[+]?\s*infinity\b/gi, 'INF')
    .replace(/[+]?\s*inf\b/gi, 'INF');
};

/**
 * Parse interval notation string into a number line element descriptor.
 *
 * Supported formats:
 *   [1, 10)    → interval, start inclusive, end exclusive
 *   (-inf, 0]  → ray going left from 0, origin inclusive
 *   [3, +inf)  → ray going right from 3, origin inclusive
 *   5  or 1/2  → point
 *
 * @param {string} input - The notation string
 * @returns {{ valid: boolean, element?: object, error?: string }}
 */
export const parseIntervalNotation = (input) => {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter a value' };
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return { valid: false, error: 'Please enter a value' };
  }

  // Normalize infinity symbols before matching
  const normalized = normalizeInfinity(trimmed);

  // Check for bracket notation: [a, b) or (a, b]  etc.
  const bracketMatch = normalized.match(/^([[(])\s*(.+?)\s*,\s*(.+?)\s*([\])])$/);
  if (bracketMatch) {
    const leftBracket = bracketMatch[1];
    const leftStr = bracketMatch[2].trim();
    const rightStr = bracketMatch[3].trim();
    const rightBracket = bracketMatch[4];

    const leftIsNegInf = /^-INF$/i.test(leftStr);
    const rightIsInf = /^INF$/i.test(rightStr);

    if (leftIsNegInf && rightIsInf) {
      return { valid: false, error: 'Both endpoints cannot be infinity' };
    }

    if (leftIsNegInf) {
      // Ray going left: (-inf, value] or (-inf, value)
      const rightResult = parseFraction(rightStr);
      if (!rightResult.valid) {
        return { valid: false, error: `Invalid value: ${rightResult.error}` };
      }
      return {
        valid: true,
        element: {
          type: 'ray',
          origin: rightResult.value,
          direction: 'left',
          originOpen: rightBracket === ')',
          originLabel: rightResult.display,
        },
      };
    }

    if (rightIsInf) {
      // Ray going right: [value, +inf) or (value, +inf)
      const leftResult = parseFraction(leftStr);
      if (!leftResult.valid) {
        return { valid: false, error: `Invalid value: ${leftResult.error}` };
      }
      return {
        valid: true,
        element: {
          type: 'ray',
          origin: leftResult.value,
          direction: 'right',
          originOpen: leftBracket === '(',
          originLabel: leftResult.display,
        },
      };
    }

    // Finite interval: [a, b)
    const leftResult = parseFraction(leftStr);
    if (!leftResult.valid) {
      return { valid: false, error: `Invalid start value: ${leftResult.error}` };
    }
    const rightResult = parseFraction(rightStr);
    if (!rightResult.valid) {
      return { valid: false, error: `Invalid end value: ${rightResult.error}` };
    }

    if (leftResult.value >= rightResult.value) {
      return { valid: false, error: 'Start must be less than end' };
    }

    return {
      valid: true,
      element: {
        type: 'interval',
        start: leftResult.value,
        end: rightResult.value,
        startOpen: leftBracket === '(',
        endOpen: rightBracket === ')',
        startLabel: leftResult.display,
        endLabel: rightResult.display,
      },
    };
  }

  // No bracket pattern — try as a single point value
  const pointResult = parseFraction(trimmed);
  if (pointResult.valid) {
    return {
      valid: true,
      element: { type: 'point', value: pointResult.value, label: pointResult.display },
    };
  }

  return { valid: false, error: 'Invalid notation. Use [a, b), (-inf, a], or a number.' };
};

/**
 * Snap element values to nearest tick and validate they are within bounds.
 * Returns an error string if invalid, or null if valid.
 * Mutates the element values in place (snapping).
 *
 * @param {Object} element - Element descriptor (point/interval/ray)
 * @param {Object} config - Number line config (min, max, step, snapToTicks)
 * @returns {string|null} - Error message or null
 */
export const snapAndValidateBounds = (element, config) => {
  const { min, max, step, snapToTicks = true } = config;

  const snap = (v) => {
    if (snapToTicks && step) {
      return Math.round(v / step) * step;
    }
    return v;
  };

  const round = (v) => Math.round(v * 1000) / 1000;

  const checkBounds = (v, label) => {
    if (v < min || v > max) {
      return `${label} (${v}) is outside range [${min}, ${max}]`;
    }
    return null;
  };

  // Snap-to-tick rounds typed values like 1/3 to the nearest tick (e.g. 0.33),
  // which would otherwise cause labelStillValid's tight default epsilon to
  // discard the student's typed label. Allow a label to survive if it parses
  // back within half a snap step of the snapped value.
  const labelEpsilon = snapToTicks && step ? step / 2 : undefined;

  if (element.type === 'point') {
    element.value = round(snap(element.value));
    if (!sharedLabelStillValid(element.label, element.value, labelEpsilon)) {
      delete element.label;
    }
    return checkBounds(element.value, 'Value');
  }

  if (element.type === 'interval') {
    element.start = round(snap(element.start));
    element.end = round(snap(element.end));
    if (!sharedLabelStillValid(element.startLabel, element.start, labelEpsilon)) {
      delete element.startLabel;
    }
    if (!sharedLabelStillValid(element.endLabel, element.end, labelEpsilon)) {
      delete element.endLabel;
    }
    if (element.start >= element.end) {
      return 'Start and end snap to the same tick — use a wider interval or a point';
    }
    return checkBounds(element.start, 'Start') || checkBounds(element.end, 'End');
  }

  if (element.type === 'ray') {
    element.origin = round(snap(element.origin));
    if (!sharedLabelStillValid(element.originLabel, element.origin, labelEpsilon)) {
      delete element.originLabel;
    }
    return checkBounds(element.origin, 'Origin');
  }

  return null;
};
